import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { images } from 'utils/icons';
import { ICON_TEXT_TYPE, MAP_CONFIG } from 'utils/constants/Map';
import MarkerPopup from '../InfoWindow/InfoWindowContent/InfoWindowContent';
import { createRoot } from 'react-dom/client';
import isEmpty from 'lodash/isEmpty';
import { isBase64 } from 'utils/checkImage';

function getMarkerIcon({ seq, iconSeqFillColor = '#929598', iconTextType }) {
  let svg;
  switch (iconTextType) {
    case ICON_TEXT_TYPE.number:
      svg = `${'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
        '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
        '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
        `<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="${iconSeqFillColor}" stroke="#FFFFFF" stroke-width="10" mask="url(#mask-2)" xlink:href="#path-1"/>` +
        '<text id="1" fill="#FFFFFF" font-family="Source Sans Pro" font-size="48" font-weight="400" text-anchor="middle" x="50%" y="60%">'}${seq}</text> </g> </g> </g> </svg>`;
      break;

    case ICON_TEXT_TYPE.fillOnly:
      svg =
        `${'<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
          '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
          '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
          `<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="${iconSeqFillColor}" stroke="#FFFFFF" stroke-width="10" mask="url(#mask-2)" xlink:href="#path-1"/>`}` +
        '</g> </g> </g> </svg>';
      break;

    default:
      svg =
        '<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="200" height="200" viewBox="0 0 100 100">' +
        '<defs> <circle id="path-1" cx="50" cy="50" r="50"/><mask id="mask-2" width="30" height="30" x="0" y="0" fill="white">' +
        '<use xlink:href="#path-1"/> </mask> </defs> <g id="Page-1" fill="none" fill-rule="evenodd"> <g id="Phone-Portrait---320" transform="translate(-209 -51)">' +
        '<g id="Group" transform="translate(209 51)"> <use id="Circle" fill="#FFFFFF" stroke="#000000" stroke-width="14" mask="url(#mask-2)" xlink:href="#path-1"/>' +
        '</g> </g> </g> </svg>';
      break;
  }

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const Marker = ({
  maps,
  map,
  position,
  markerRef,
  detailOpen,
  markerHasOverlay,
  icon,
  text,
  markerDraggable,
  markerDragStart,
  markerDragEnd,
  markerClick: handleMarkerClick,
  iconOverlay,
  iconSelected,
  iconSeqFillColor,
  iconTextType,
  infoWindow,
  switchedLayer,
  layers,
  allMarkers = [],
  markerInfowindowRef,
  distress,
}) => {
  // This methd checks if the marker exists with the same ID and position lat lng
  const checkIfMarkerExists = position => {
    const exist = Object.keys(markerRef.current).find(layer => {
      const layerMarkers = markerRef.current[layer];
      return layerMarkers.some(s => {
        const lat = s?.getPosition().lat();
        const lng = s?.getPosition().lng();
        const match =
          (lat === position.lat || lng === position.lng) &&
          s.id === position.id &&
          !s.overlay;
        // for multiEvent markers
        if (position?.multiEvent) {
          const multiEventMatch =
            s.id === position.id && s.eventId === position.eventId;
          return multiEventMatch;
        }

        return match;
      });
    });
    return exist;
  };
  const removeObjectWithId = (arr, id) => {
    const objWithIdIndex = arr.findIndex(obj => obj.id === id);
    if (objWithIdIndex > -1) {
      arr.splice(objWithIdIndex, 1);
    }
    return arr;
  };
  // This method removes all the halo markers from the list
  const removeHaloMarkersFromList = position => {
    const eachLayer = markerRef.current[position.layer];
    if (isEmpty(eachLayer)) return;
    eachLayer.forEach((f, index) => {
      if (f.overlay) {
        // remove it frm the map and ref
        f.setMap(null);
        markerRef.current[position.layer].splice(index, 1);
      }
    });
  };

  // This method adds a new marker to the maps
  const addNewMarker = position => {
    const marker = new maps.Marker({
      position,
      maps,
      id: position.id,
      eventId: position?.eventId,
      lat: position.lat,
      lng: position.lng,
      name: position.name,
      draggable: markerDraggable,
      layer: position.layer,
      overlay: position.overlay,
      icon: {
        url: getIcon({
          icon,
          text,
          iconOverlay,
          iconSeqFillColor,
          iconTextType:
            iconTextType || (Number.isInteger(text) ? 'number' : 'outlined'),
        }),
        anchor: getAnchor(iconOverlay),
        scaledSize: getScaledSize(iconOverlay),
        origin: new maps.Point(0, 0),
      },
      shape,
      zIndex: iconSelected
        ? MAP_CONFIG.overlayMarker.zIndex
        : iconOverlay
        ? MAP_CONFIG.overlayMarker.zIndex - 1
        : MAP_CONFIG.normalMarker.zIndex,
    });
    marker.setMap(map);
    maps.event.addListener(marker, 'dragstart', manageDragEvent);
    maps.event.addListener(marker, 'dragend', markerDragEndEvent);

    maps.event.addListener(marker, 'click', e => {
      if (distress) {
        return;
      }
      // close all other info windows
      markerInfowindowRef.current.infowindow.forEach(item => item.close());
      // set the position of the infowindow
      infoWindow.setPosition(e.latLng);

      infoWindow.setMap(map);
    });
    return marker;
  };

  // this method adds a marker to the ref
  const addMarkerToRef = (marker, markerHasOverlay) => {
    if (markerHasOverlay) {
      markerRef.current[markerHasOverlay.layer].push(marker);

      return;
    }
    // keep track of markers
    if (markerRef.current[position.layer]) {
      markerRef.current[position.layer].push(marker);
    } else {
      Object.assign(markerRef.current, {
        [position.layer]: [],
      });
      markerRef.current[position.layer].push(marker);
    }
  };

  const shape = {
    coords: [1, 1, 1, 20, 18, 20, 18, 1],
    type: 'poly',
  };

  const getIcon = ({
    icon,
    text,
    iconOverlay,
    iconSeqFillColor,
    iconTextType,
  }) => {
    if (icon) {
      if (iconOverlay) {
        const imgIsBase64 = isBase64(images[`${icon}Overlay`]);
        return imgIsBase64
          ? images[`${icon}Overlay`]
          : images[`${icon}Overlay`].default;
      } else {
        const imgIsBase64 = isBase64(images[icon]); // some icons like responder are png instead of svg, so they are base64 type and not conponent like
        return typeof icon === 'string' && icon.includes('https') // images now comes from api in tasks
          ? icon
          : imgIsBase64
          ? images[icon]
          : images[icon]?.default;
      }
    } else {
      return getMarkerIcon({ seq: text, iconSeqFillColor, iconTextType });
    }
  };
  const getScaledSize = iconOverlay => {
    if (iconOverlay) {
      return new maps.Size(
        MAP_CONFIG.overlayMarker.scaledSize,
        MAP_CONFIG.overlayMarker.scaledSize,
      );
    } else {
      return new maps.Size(
        MAP_CONFIG.normalMarker.scaledSize,
        MAP_CONFIG.normalMarker.scaledSize,
      );
    }
  };
  const getAnchor = iconOverlay => {
    if (iconOverlay) {
      return new maps.Point(
        MAP_CONFIG.overlayMarker.anchor,
        MAP_CONFIG.overlayMarker.anchor,
      );
    } else {
      return new maps.Point(
        MAP_CONFIG.normalMarker.anchor,
        MAP_CONFIG.normalMarker.anchor,
      );
    }
  };

  // This hook removes the layers
  useEffect(() => {
    if (
      !isEmpty(markerRef.current) &&
      Object.keys(layers).some(k => !layers[k])
    ) {
      switchedLayer.forEach(f => {
        if (!markerRef.current[f]) {
          markerRef.current[f] = [];
        }
        const copy = [...markerRef.current[f]];
        copy.forEach((marker, i) => {
          if (f === marker.layer) {
            marker.setMap(null);
            markerRef.current[f].splice(i, 1);
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, map, position]);

  // NOTE-if adding any new page that should display the marker and halo, make sure detail panel state is set in redux
  // this hook removes halo marker if it exists in the marker array ref and if the detail panel is closed
  useEffect(() => {
    if (!detailOpen) {
      removeHaloMarkersFromList(position);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailOpen, position]);

  // This method checks if a a marker halo exixts in a layer
  const checkIfMarkerHasOverlay = (position, markerHasOverlay) => {
    if (!markerHasOverlay) {
      return false;
    }

    const markerFound = markerRef.current[markerHasOverlay.layer].find(f => {
      return f.id === position.id; // no need to add the highlight marker if the base marker is not found
    });

    if (!markerFound) {
      return false;
    }
    return !markerRef.current[markerHasOverlay.layer].find(f => {
      return f.id === position.id && f.overlay; // for adding overlays, check that there should be no id and overlay for the position
    });
  };

  //This hook adds overlay/halo (not markers) to the existing marker if it does not exist
  useEffect(() => {
    if (
      markerRef?.current &&
      position.overlay &&
      checkIfMarkerHasOverlay(position, markerHasOverlay) &&
      !switchedLayer.includes(position.layer)
    ) {
      // add overlay to refs and maps
      const marker = addNewMarker(position);
      addMarkerToRef(marker, markerHasOverlay);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, map]);

  const manageDragEvent = useCallback(
    evt => {
      if (markerDragStart) {
        markerDragStart(evt);
      }
    },
    [markerDragStart],
  );

  const markerDragEndEvent = useCallback(
    evt => {
      if (markerDragEnd) {
        markerDragEnd(evt);
      }
    },
    [markerDragEnd],
  );

  // This method adds the markers (not halo) on the map only if it does not exist
  useEffect(() => {
    // store markers in the ref if it doesnt exist
    if (
      markerRef?.current &&
      !checkIfMarkerExists(position) &&
      !switchedLayer.includes(position.layer) &&
      !position.overlay
    ) {
      const marker = addNewMarker(position);
      // keep track of info windows
      if (markerInfowindowRef.current?.infowindow) {
        markerInfowindowRef.current.infowindow.push(infoWindow);
      } else {
        Object.assign(markerInfowindowRef.current, {
          infowindow: [],
        });
        markerInfowindowRef.current.infowindow.push(infoWindow);
      }
      /// set the marker in the correct layer
      addMarkerToRef(marker);
      if (distress) {
        return;
      }
      const content = document.createElement('div');
      const root = createRoot(content);
      root.render(React.createElement(MarkerPopup, { position }, null));

      // set infowindow content
      infoWindow.setContent(content);
      // attach listener to infowindow
      maps.event.addListener(infoWindow, 'domready', () => {
        const someButton = document.getElementById(
          `btn-click-${position.pageName}`,
        );
        if (someButton && position?.pageName) {
          someButton.addEventListener('click', e => {
            //close the infowindow
            infoWindow.setMap(null);
            infoWindow.close();
            if (typeof handleMarkerClick === 'function') {
              handleMarkerClick({
                event: e,
                id: position?.eventId ? position.eventId : position.id,
                pageName: position.pageName,
                position,
              });
            }
            maps.event.trigger(map, 'click', e);
          });
        }
      });
    }
    /* eslint-disable consistent-return */
    return () => {
      infoWindow.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, switchedLayer, infoWindow, markerInfowindowRef, distress]);

  const findMarkersNotInRef = () => {
    if (isEmpty(allMarkers)) return;
    Object.keys(layers)
      .filter(f => f !== 'undefined')
      .filter(f => layers[f])
      .forEach(f => {
        if (!markerRef.current[f]) {
          markerRef.current[f] = [];
        }
        const copy = [...markerRef.current[f]]; // eg. events array has [1,2,3]
        if (isEmpty(copy)) return;

        //-----------incase the position is updated for the same id remove the old marker----------------------
        const found = copy.find(f => {
          const lat = f?.getPosition().lat();
          const lng = f?.getPosition().lng();
          const latMatch = lat !== position.lat;
          const lngMatch = lng !== position.lng;
          if (position?.multiEvent) {
            const multiEventMatch =
              f.id === position.id &&
              f.eventId === position.eventId &&
              latMatch &&
              lngMatch;
            return multiEventMatch;
          }
          return f.id === position.id && latMatch && lngMatch;
        });

        if (found) {
          // found.setMap(null);
          // removeObjectWithId(copy, found.id);
          // add overlay to refs and maps
          for (const key in markerRef.current) {
            if (Object.hasOwnProperty.call(markerRef.current, key)) {
              const eachLayer = markerRef.current[key];
              eachLayer.forEach((f, index) => {
                // remove it frm the map and ref
                f.setMap(null);
                markerRef.current[key].splice(index, 1);
              });
            }
          }
          // keep track of info windows
          if (markerInfowindowRef.current?.infowindow) {
            markerInfowindowRef.current.infowindow.push(infoWindow);
          } else {
            Object.assign(markerInfowindowRef.current, {
              infowindow: [],
            });
            markerInfowindowRef.current.infowindow.push(infoWindow);
          }
          const marker = addNewMarker(position);
          addMarkerToRef(marker, markerHasOverlay);
        }
        //-------------------------------------------------------------------------------------------------------
        //---------In case page is changed and new markers comes in remove the old from ref----------------------
        //---------the marker is updated then remove previous and add new
        const filteredItems = copy.filter(
          // needs to be removed  [2,3]
          f => !allMarkers.some(a => a.id === f.id),
        );
        // [1,2,3] => [1]
        if (!isEmpty(filteredItems)) {
          filteredItems.forEach(item => {
            item.setMap(null);
            removeObjectWithId(markerRef.current[f], item.id);
          });
        }
      });
  };
  // check and compare the markers in ref, delete the markers not in position from marker ref
  useEffect(() => {
    findMarkersNotInRef();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMarkers, position]);

  return null;
};

Marker.defaultProps = {
  type: 'basic',
  markerDraggable: false,
};

Marker.propTypes = {
  type: PropTypes.oneOf(['text', 'basic']),
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  icon: PropTypes.string,
  maps: PropTypes.object,
  map: PropTypes.object,
  position: PropTypes.object,
  infoWindow: PropTypes.any,
  markerRef: PropTypes.oneOfType([PropTypes.shape({ current: PropTypes.any })]),
  layers: PropTypes.object,
  markerHasOverlay: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  markerDraggable: PropTypes.bool,
  markerDragStart: PropTypes.func,
  markerDragEnd: PropTypes.func,
  markerClick: PropTypes.func,
  iconOverlay: PropTypes.bool,
  detailOpen: PropTypes.bool,
  iconSelected: PropTypes.bool,
  iconSeqFillColor: PropTypes.string,
  iconTextType: PropTypes.string,
  switchedLayer: PropTypes.array,
  allMarkers: PropTypes.array,
  markerInfowindowRef: PropTypes.oneOfType([
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  distress: PropTypes.bool,
};

export default Marker;
